import { useCallback, useEffect, useState } from 'react';

/**
 * Put a string on the clipboard, and say briefly that it went.
 *
 * <p>The MECHANISM only, and no markup -- the same split houndup's use-copy
 * makes, and for the same reason: the places that copy want to look nothing
 * alike. The join code panel says "Copied" in a word beside an icon; a copier
 * that ever lands in a table row will want the tick to replace the glyph
 * instead, because a word appearing mid-row shoves the rest of the line along
 * while somebody is reading it.
 *
 * WHY THIS IS NOT navigator.clipboard ALONE. That API is undefined outside a
 * secure context, which is not a hypothetical here: esfront is served over
 * plain http on the compose stack (3080) and by the webpack dev server (3000),
 * and a GM reading the panel on either would press the icon and get nothing at
 * all. The execCommand path below is deprecated and is the only thing that
 * works there, so both are kept and the modern one is tried first.
 *
 * WHAT MUST NOT HAPPEN is saying it was copied when it was not. Every path that
 * sets `copied` is a path that actually reached a clipboard; a refusal -- a
 * private window, a permissions policy, a browser that has both APIs and allows
 * neither -- leaves the word off and the person presses again, which is the
 * right failure. This is the one thing to preserve when editing below.
 *
 * ⚠️ EsChangelog HAS ITS OWN COPY AND IS DELIBERATELY NOT FOLDED IN HERE. Its
 * `copied` is a release VERSION rather than a boolean, because the changelog
 * draws a permalink on every release on the page and has to know WHICH one was
 * pressed -- one hook instance per row is not available to it, the rows come
 * out of a map. Generalising this to carry a key would make the common case
 * (one copier, one target) carry an argument it never uses. The duplication is
 * the execCommand fallback and nothing else, and it is written down in both
 * places rather than shared badly.
 *
 * @param pSettle how long the acknowledgement stays up, in ms
 * @returns { copied, copy } -- a boolean and an async function taking the text
 */
export const useEsClipboard = ( pSettle = 1600 ) => {
    // A count of good presses rather than a boolean, so the effect below sees
    // every press: a second true over a first one is no state change, and the
    // first press's timer took the word down mid-way through the second's.
    const [ presses, setPresses ] = useState( 0 );

    // Taken back down on a timer, RESTARTED rather than stacked when somebody
    // presses twice: two live timers would clear the word while the second
    // press was still the most recent thing that happened, which reads as the
    // copy having failed.
    useEffect( () => {
        if ( !presses ) return undefined;

        const aDone = setTimeout( () => setPresses( 0 ), pSettle );
        return () => clearTimeout( aDone );
    }, [ presses, pSettle ] );

    const copy = useCallback( async ( pText ) => {
        if ( pText === null || pText === undefined || pText === '' ) return false;

        const aText = String( pText );

        if ( navigator.clipboard?.writeText ) {
            try {
                await navigator.clipboard.writeText( aText );
                setPresses( ( pWas ) => pWas + 1 );
                return true;
            }
            catch {
                // Falls through. The modern API being present does not mean it
                // is permitted, and a refusal here is exactly the case the old
                // path still covers.
            }
        }

        if ( fallbackCopy( aText ) ) {
            setPresses( ( pWas ) => pWas + 1 );
            return true;
        }

        setPresses( 0 );
        return false;
    }, [] );

    return { copied: presses > 0, copy };
};

/**
 * The pre-clipboard way: a textarea off screen, selected, and execCommand.
 *
 * <p>Deprecated, and the only thing that works without a secure context. Lifted
 * from EsChangelog, which has carried it since the changelog got permalinks.
 *
 * <p>readonly and the fixed/opacity-0 positioning are not decoration: without
 * readonly a mobile browser opens its keyboard over the page, and without the
 * positioning the textarea scrolls the document to wherever it was appended.
 *
 * @returns true if the copy actually happened
 */
function fallbackCopy( pText ) {
    const aEl = document.createElement( 'textarea' );

    aEl.value = pText;
    aEl.setAttribute( 'readonly', '' );
    aEl.style.position = 'fixed';
    aEl.style.opacity = '0';
    document.body.appendChild( aEl );
    aEl.select();

    try {
        return document.execCommand( 'copy' );
    }
    catch {
        return false;
    }
    finally {
        document.body.removeChild( aEl );
    }
}
